// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SoundManager from '../utils/system/SoundManager';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';
import './ShipComputerModal.css';

const SUGGESTIONS = [
  'How does warp drive work?',
  'What is the Fermi paradox?',
  'Tell me about Magrathea',
  'What aliens are in the Pokémon universe?',
  'What is the answer to life the universe and everything?',
  'Which systems have upgrades?',
  'Tell me about black holes',
];

export default function ShipComputerModal({ onClose, initialQuery }) {
  const { token, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([
    { role: 'computer', text: 'SHIP COMPUTER ONLINE. Query the game database or the Hitchhiker\'s Guide to the Galaxy. How can I assist, Captain?' }
  ]);
  const [input, setInput] = useState(initialQuery || '');
  const [loading, setLoading] = useState(false);
  const [kb, setKb] = useState('game'); // 'game' | 'guide'
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const query = async (text) => {
    if (!text.trim() || loading) return;

    if (!isAuthenticated) {
      setMessages(prev => [...prev, {
        role: 'computer',
        text: 'ACCESS DENIED: Ship Computer requires authentication. Please log in, Captain.'
      }]);
      return;
    }

    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);

    try {
      const data = await api.kb.query(token, kb, text);
      const answer = data.answer || 'No data found on that subject, Captain.';
      setMessages(prev => [...prev, { role: 'computer', text: answer }]);
      SoundManager.play('scan_complete', { priority: 'low' });
    } catch (err) {
      console.error('[ShipComputer] Query failed:', err);
      setMessages(prev => [...prev, { role: 'computer', text: `COMMS ERROR: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      query(input);
    }
  };

  return createPortal(
    <div className="ship-computer-overlay" onClick={onClose}>
      <div className="ship-computer-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sc-header">
          <div className="sc-title">
            <span className="sc-icon">🖥</span>
            SHIP COMPUTER
          </div>
          <div className="sc-kb-toggle">
            <button
              className={`sc-kb-btn ${kb === 'game' ? 'active' : ''}`}
              onClick={() => setKb('game')}
            >GAME DATA</button>
            <button
              className={`sc-kb-btn ${kb === 'guide' ? 'active' : ''}`}
              onClick={() => setKb('guide')}
            >GUIDE</button>
          </div>
          <button className="sc-close" onClick={onClose}>✕</button>
        </div>

        {/* Messages */}
        <div className="sc-messages">
          {messages.map((m, i) => (
            <div key={i} className={`sc-message sc-${m.role}`}>
              <span className="sc-prefix">{m.role === 'computer' ? '◈' : '▶'}</span>
              <span className="sc-text">{m.text}</span>
            </div>
          ))}
          {loading && (
            <div className="sc-message sc-computer sc-loading">
              <span className="sc-prefix">◈</span>
              <span className="sc-text">Processing query<span className="sc-dots">...</span></span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        <div className="sc-suggestions">
          {SUGGESTIONS.slice(0, 4).map((s, i) => (
            <button key={i} className="sc-suggestion" onClick={() => query(s)}>{s}</button>
          ))}
        </div>

        {/* Input */}
        <div className="sc-input-row">
          <input
            ref={inputRef}
            className="sc-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask the ship computer..."
            disabled={loading}
          />
          <button className="sc-send" onClick={() => query(input)} disabled={loading || !input.trim()}>
            SEND
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
